import type { ButtonHTMLAttributes, ReactNode } from "react";

export type OrbTone = "neutral" | "teal" | "pink" | "gold";
export type OrbSize = "xs" | "sm" | "md" | "lg" | "xl";

const orbToneStyles: Record<OrbTone, { bg: string; ring: string; glow: string }> = {
  neutral: {
    bg: "bg-[radial-gradient(ellipse_at_30%_20%,rgba(180,190,200,0.18)_0%,rgba(20,40,55,0.85)_55%,rgba(10,25,38,0.95)_100%)]",
    ring: "ring-white/[0.08]",
    glow: "shadow-[0_0_20px_rgba(140,160,180,0.08)]",
  },
  teal: {
    bg: "bg-[radial-gradient(ellipse_at_30%_20%,rgba(40,140,140,0.15)_0%,rgba(10,35,50,0.9)_55%,rgba(5,20,30,0.95)_100%)]",
    ring: "ring-[#6FAB1B]/[0.08]",
    glow: "shadow-[0_0_24px_rgba(111,171,27,0.06)]",
  },
  pink: {
    bg: "bg-[radial-gradient(ellipse_at_35%_25%,rgba(180,100,160,0.22)_0%,rgba(60,25,55,0.8)_50%,rgba(15,20,35,0.95)_100%)]",
    ring: "ring-pink-400/[0.12]",
    glow: "shadow-[0_0_28px_rgba(220,120,180,0.12)]",
  },
  gold: {
    bg: "bg-[radial-gradient(ellipse_at_30%_20%,rgba(254,198,1,0.14)_0%,rgba(40,30,10,0.85)_55%,rgba(10,15,20,0.95)_100%)]",
    ring: "ring-fwf-yellow/10",
    glow: "shadow-[0_0_24px_rgba(254,198,1,0.08)]",
  },
};

const orbSizeClass: Record<OrbSize, string> = {
  xs: "h-6 w-6",
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-14 w-14",
  xl: "h-20 w-20",
};

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  tone?: OrbTone;
  size?: OrbSize;
  /** Rotating gold border halo — use for states that need attention */
  glow?: boolean;
  /** Render as a decorative span instead of a button (use when nested inside a button) */
  decorative?: boolean;
  children: ReactNode;
};

/**
 * Presentational glass orb — radial-gradient body + specular highlight + optional rotating glow.
 * Used for primary accents like the InputArea send button and Sidebar "New chat" trigger.
 */
export function GlassOrb({
  tone = "neutral",
  size = "sm",
  glow = false,
  decorative = false,
  children,
  className = "",
  type = "button",
  disabled,
  ...rest
}: Props) {
  const s = orbToneStyles[tone];
  const sharedClassName = `group/orb relative inline-flex items-center justify-center rounded-full ring-1 transition-all duration-200 ${orbSizeClass[size]} ${s.bg} ${s.ring} ${s.glow} ${className}`;

  const inner = (
    <>
      {/* Rotating border glow */}
      {glow && <span className="preview-glow absolute inset-[-2px] pointer-events-none rounded-full" />}
      {/* Hover wash — soft white bloom that fades in */}
      <span className="absolute inset-0 rounded-full bg-white/0 transition-colors duration-200 group-hover/orb:bg-surface-hover pointer-events-none" />
      {/* Top-left specular highlight */}
      <span className="absolute inset-0 rounded-full bg-[radial-gradient(ellipse_35%_25%_at_28%_18%,rgba(255,255,255,0.09)_0%,transparent_70%)] pointer-events-none" />
      {/* Content */}
      <span className="relative text-white/90 group-hover/orb:text-white transition-colors duration-200">{children}</span>
    </>
  );

  if (decorative) {
    return <span className={sharedClassName}>{inner}</span>;
  }

  return (
    <button
      {...rest}
      type={type}
      disabled={disabled}
      className={`${sharedClassName} hover:scale-105 hover:brightness-125 hover:ring-white/[0.16] active:scale-95 active:brightness-100 disabled:opacity-40 disabled:hover:scale-100 disabled:hover:brightness-100 disabled:hover:ring-white/[0.08] disabled:cursor-not-allowed`}
    >
      {inner}
    </button>
  );
}
